/**
 * The care schedule — the portal's headline feature.
 *
 * Everything here is DERIVED from the dates on the record. The prototype originally spelled these
 * out ("Every 3 months · next due 1 August") and one literal was simply wrong: flea was written as
 * 18 August when 18 July + 4 weeks is 15 August. Deriving it is what makes the 57 editable fields
 * mean anything, and it is why the reminders screen and Home's "Coming up" cannot disagree — they
 * read the same array.
 *
 * WHAT CHANGED FOR REAL DATA (and why)
 * ------------------------------------
 * The prototype carried three items with invented progress values — grooming at 64%, contact
 * review at 55%, feeding at 100% — because no field existed behind them. Against 199 real records
 * that would draw a confident ring over nothing. Here an item is emitted ONLY when the record
 * actually carries the data for it. An absent schedule is absent, not a decorative 64%.
 */
import { daysUntil, fmtDay, parseDay } from './dates.js';
/**
 * The reader-facing name for each kind, defined ONCE.
 *
 * The reminders screen also has to name a treatment that is ABSENT from the schedule ("we don't
 * have your worming dates yet"), so it cannot read the name off a CareItem that does not exist.
 * Without this export it kept its own copy of the four strings, and renaming one here would have
 * silently desynced the two lists with nothing to catch it.
 */
export const CARE_ITEM_NAMES = {
    worming: 'Worming',
    flea: 'Flea treatment',
    booster: 'Annual booster',
    kennel: 'Kennel cough',
    feeding: 'Daily feeding',
    contact: 'Contact review',
};
/**
 * Add a parsed interval to a date.
 *
 * Accepts the registration form's own dropdown wording verbatim: "Every 4 weeks", "Every month",
 * "Every 3 months", "Every 6 months", "Every 12 months". Returns null for "Other / not sure",
 * which is a real option on the live form and genuinely has no due date — the caller turns that
 * into "Tell us how often" rather than a blank.
 */
export function addEvery(from, every) {
    if (!from)
        return null;
    const match = /(\d+)?\s*(week|month|year)/i.exec(every ?? '');
    if (!match)
        return null;
    const unit = match[2];
    if (unit === undefined)
        return null;
    const n = Number(match[1] ?? 1);
    if (!Number.isFinite(n) || n <= 0)
        return null;
    const d = new Date(from.getTime());
    const lower = unit.toLowerCase();
    if (lower === 'week') {
        d.setDate(d.getDate() + n * 7);
    }
    else if (lower === 'year') {
        d.setFullYear(d.getFullYear() + n);
    }
    else {
        // setMonth clamps: 31 Jan + 1 month lands on 28/29 Feb, which is the behaviour we want for a
        // recurring treatment (it never skips a month by rolling into the next one).
        const targetMonth = d.getMonth() + n;
        const dayOfMonth = d.getDate();
        d.setDate(1);
        d.setMonth(targetMonth);
        const lastDayOfTarget = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
        d.setDate(Math.min(dayOfMonth, lastDayOfTarget));
    }
    return d;
}
/** Turn a day gap into the words the screen shows. */
export function dueWord(days) {
    if (days == null)
        return 'No date set';
    if (days < 0) {
        const n = Math.abs(days);
        return `${n} ${n === 1 ? 'day' : 'days'} overdue`;
    }
    if (days === 0)
        return 'Due today';
    if (days === 1)
        return 'Due tomorrow';
    if (days <= 45)
        return `In ${days} days`;
    return 'Up to date';
}
/**
 * Fraction of the current cycle already elapsed, 0-100 — what the progress ring draws.
 *
 * When there is no last-given date (booster and kennel cough carry only a next-due date), the
 * cycle is assumed to have started a year before the due date, which is the real interval for both.
 */
export function cyclePct(last, due, today) {
    if (!due)
        return null;
    let from = last;
    if (!from) {
        from = new Date(due.getTime());
        from.setFullYear(from.getFullYear() - 1);
    }
    const span = due.getTime() - from.getTime();
    if (span <= 0)
        return 100;
    const elapsed = today.getTime() - from.getTime();
    return Math.max(0, Math.min(100, Math.round((elapsed / span) * 100)));
}
/**
 * Build the whole schedule, soonest-first for the items that have a due date.
 *
 * An item is omitted entirely when the record carries nothing for it. That is the deliberate
 * difference from the prototype, which always rendered all seven rows.
 */
export function buildCareSchedule(source, today) {
    const definitions = [
        {
            kind: 'worming',
            name: CARE_ITEM_NAMES.worming,
            interval: source.wormingInterval,
            last: parseDay(source.wormingLastGiven),
            selfAdministered: true,
        },
        {
            kind: 'flea',
            name: CARE_ITEM_NAMES.flea,
            interval: source.fleaInterval,
            last: parseDay(source.fleaLastGiven),
            selfAdministered: true,
        },
        {
            kind: 'booster',
            name: CARE_ITEM_NAMES.booster,
            interval: 'Yearly',
            last: null,
            explicitDue: parseDay(source.boosterNextDue),
            selfAdministered: false,
        },
        {
            kind: 'kennel',
            name: CARE_ITEM_NAMES.kennel,
            interval: 'Yearly',
            last: null,
            explicitDue: parseDay(source.kennelCoughNextDue),
            selfAdministered: false,
        },
        {
            kind: 'contact',
            name: CARE_ITEM_NAMES.contact,
            interval: 'Every 3 months',
            last: parseDay(source.lastReviewAt),
            explicitDue: parseDay(source.nextReviewDue),
            selfAdministered: false,
        },
    ];
    const items = [];
    for (const def of definitions) {
        const due = def.explicitDue !== undefined ? def.explicitDue : addEvery(def.last, def.interval);
        const needsInterval = !due && def.last !== null && def.selfAdministered;
        // Nothing on the record for this item at all — omit it rather than draw an empty ring.
        if (!due && !needsInterval)
            continue;
        const daysLeft = daysUntil(due, today);
        const percent = cyclePct(def.last, due, today);
        const status = due ? dueWord(daysLeft) : 'Tell us how often';
        const urgency = needsInterval || (daysLeft != null && daysLeft <= 14) ? 'attention' : 'calm';
        items.push({
            kind: def.kind,
            name: def.name,
            due,
            daysLeft,
            status,
            hasStatus: true,
            percent,
            detail: due && def.interval ? `${def.interval} · next due ${fmtDay(due)}` : (def.interval ?? ''),
            urgency,
            selfAdministered: def.selfAdministered,
            needsInterval,
        });
    }
    // Feeding is a daily routine, not a due date. It appears only when the meal record is linked.
    if (source.feedingSummary) {
        items.push({
            kind: 'feeding',
            name: CARE_ITEM_NAMES.feeding,
            due: null,
            daysLeft: null,
            status: '',
            hasStatus: false,
            percent: 100,
            detail: source.feedingSummary,
            urgency: 'calm',
            selfAdministered: false,
            needsInterval: false,
        });
    }
    return items;
}
/**
 * Home's "Coming up" — the same schedule, soonest first, capped.
 *
 * Reads `buildCareSchedule` output rather than recomputing, so the front page can never disagree
 * with the reminders screen. Daily routines are excluded: they have no "next".
 */
export function comingUp(items, limit = 4) {
    return items
        .filter((item) => item.kind !== 'feeding' && item.kind !== 'contact')
        .slice()
        .sort((a, b) => (a.due ? a.due.getTime() : Infinity) - (b.due ? b.due.getTime() : Infinity))
        .slice(0, limit);
}
//# sourceMappingURL=care.js.map